import { Box, Avatar, Button, Typography, Tooltip, useTheme } from "@mui/material";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { DateTime } from "luxon";
import type { MouseEvent } from "react";
import { useMemo, useRef, useState } from "react";
import { BaseModalComponent } from "../../../../common/BaseModalComponent";
import { EventJobModal } from "../calendar-container/modal/components/single-event/EventJobModal";
import { timelineMinutes, visibleDaySegment } from "./visibleDaySegment";
import { formatCompanyInstant, isRepeatedCompanyInstant } from "../../utils/companyWallTime";

// ─── Types ───────────────────────────────────────────────────────────────────

interface WorkforceItem {
    firstName: string;
    lastName: string;
    avatar: string | null;
    WorkforcesByJobs?: { jobWorkforceRole: string };
    WorkforcesByJobEvents?: {
        status: string;
        workforceUid: string;
        jobEventUid: string;
    };
}

interface CalendarEvent {
    id: string;
    title: string;
    start: string | Date;
    end: string | Date;
    extendedProps: {
        jobId?: number;
        status?: string;
        color?: string;
        workType?: string;
        client?: { name: string; avatar?: string | null };
        coverageArea?: { address: string; city: string; state: string };
        workforce?: WorkforceItem[];
        event?: { startDate: string; endDate: string };
    };
}

interface DayTimelineViewProps {
    events: CalendarEvent[];
    currentDay: Dayjs;
    isFromJob?: boolean;
    onCreateJobAtDate?: (date: Date) => void;
    timeZone?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const HOUR_START = 0;
const HOUR_END = 24;
const ROW_HEIGHT_MULTIPLE = 7.5;
const TOTAL_HOURS = HOUR_END - HOUR_START;
const ROW_HEIGHT = 72;
const HEADER_HEIGHT = 48;
const LEFT_PANEL_WIDTH = 200;
const UNASSIGNED_LABEL = "Unassigned";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const pct = (minutes: number): string =>
    `${((minutes / (TOTAL_HOURS * 60)) * 100).toFixed(4)}%`;

const statusColor = (status: string, fallback: string): string => {
    const map: Record<string, string> = {
        inProgress: "#D97706",
        scheduled: "#2563EB",
        completed: "#16A34A",
        cancelled: "#DC2626",
        assigned: "#7C3AED",
    };
    return map[status] ?? fallback ?? "#6B7280";
};

const initials = (first: string, last: string) =>
    `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase();


interface TimelineEventCardProps {
    event: CalendarEvent;
    rowIndex: number;
    color: string;
    isFromJob: boolean;
    isDark: boolean;
    hoveredKey: string | null;
    rowKey: string;
    onHover: (key: string | null) => void;
    timeZone: string;
}

const TimelineEventCard = ({
    event,
    rowIndex,
    color,
    isFromJob,
    isDark,
    hoveredKey,
    rowKey,
    onHover,
    timeZone,
}: TimelineEventCardProps) => {
    const triggerRef = useRef<HTMLButtonElement>(null);

    const startDate = new Date(event.start);
    const endDate = new Date(event.end);
    const { startMinute: startMin, endMinute: endMin } = timelineMinutes(startDate, endDate);
    const durationMin = Math.min(24 * 60 - startMin, Math.max(endMin - startMin, 30));
    const isHovered = hoveredKey === `${rowKey}-${event.id}`;
    const client = event.extendedProps?.client?.name ?? "";
    const area = event.extendedProps?.coverageArea;
    const location = area ? `${area.city}, ${area.state}` : "";
    const source = event.extendedProps?.event;
    const durationLabel = source?.startDate && source?.endDate
        ? `${formatCompanyInstant(source.startDate, timeZone)} – ${formatCompanyInstant(source.endDate, timeZone)}`
        : `${dayjs(startDate).format("h:mm A")} – ${dayjs(endDate).format("h:mm A")}`;
    const isShort = durationMin < 60;
    const jobId = event.extendedProps?.jobId;
    const eventInstanceKey = event.id || `${jobId}-${event.start}`;

    const modalArg = {
        event: {
            id: event.id,
            title: event.title,
            startStr: startDate.toISOString(),
            endStr: endDate.toISOString(),
            extendedProps: event.extendedProps,
        },
    };

    return (
        <Box
            sx={{
                position: "absolute",
                left: 0,
                right: 0,
                top: rowIndex * ROW_HEIGHT,
                height: ROW_HEIGHT,
            }}
        >
            <Tooltip
                placement="top"
                arrow
                title={
                    <Box sx={{ p: 0.5 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.8rem" }}>
                            {event.title}
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", opacity: 0.8, mt: 0.3 }}>
                            {durationLabel}
                        </Typography>
                        {client && (
                            <Typography sx={{ fontSize: "0.72rem", opacity: 0.7, mt: 0.2 }}>
                                👤 {client}
                            </Typography>
                        )}
                        {location && (
                            <Typography sx={{ fontSize: "0.72rem", opacity: 0.7 }}>
                                📍 {location}
                            </Typography>
                        )}
                    </Box>
                }
            >
                <Box
                    onClick={(event) => {
                        event.stopPropagation();
                        if (!isFromJob) triggerRef.current?.click?.();
                    }}
                    onMouseEnter={() => onHover(`${rowKey}-${event.id}`)}
                    onMouseLeave={() => onHover(null)}
                    sx={{
                        position: "absolute",
                        left: `calc(${pct(startMin)} + 2px)`,
                        width: `calc(${pct(durationMin)} - 4px)`,
                        top: 8,
                        height: ROW_HEIGHT - 10,
                        bgcolor: `${color}22`,
                        border: `1.5px solid ${color}`,
                        borderLeft: `4px solid ${color}`,
                        borderRadius: "8px",
                        cursor: isFromJob ? "default" : "pointer",
                        zIndex: 5,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        px: isShort ? 0.8 : 1.2,
                        py: 0.5,
                        transition: "transform 0.15s, box-shadow 0.15s",
                        transform: isHovered ? "scaleY(1.04)" : "scaleY(1)",
                        boxShadow: isHovered ? `0 4px 16px ${color}44` : `0 2px 6px ${color}22`,
                        backdropFilter: "blur(4px)",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: isShort ? "0.65rem" : "0.72rem",
                            fontWeight: 700,
                            color: isDark ? "#fff" : color,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            lineHeight: 1.2,
                        }}
                    >
                        {event.title}
                    </Typography>

                    {!isShort && (
                        <Typography
                            sx={{
                                fontSize: "0.62rem",
                                fontWeight: 500,
                                color: isDark ? "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.6)" : `${color}bb`,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                mt: 0.2,
                            }}
                        >
                            {durationLabel}
                        </Typography>
                    )}

                    {!isShort && client && (
                        <Typography
                            sx={{
                                fontSize: "0.6rem",
                                color: isDark ? "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.45)" : `${color}99`,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                mt: 0.1,
                            }}
                        >
                            {client}
                        </Typography>
                    )}
                </Box>
            </Tooltip>

            <BaseModalComponent
                defaultSizes={{ width: "600", height: "1000" }}
                modalLabel={`Job ${jobId}`}
                modalKey={`Jobs-Calendar-${jobId}-${eventInstanceKey}`}
                defaultFullscreen={false}
                elementTrigger={
                    <button
                        ref={triggerRef}
                        type="button"
                        style={{
                            position: "absolute",
                            left: -99999,
                            top: -99999,
                            pointerEvents: isFromJob ? "none" : "auto",
                        }}
                    />
                }
            >
                <EventJobModal arg={modalArg} />
            </BaseModalComponent>
        </Box>
    );
};

// ─── Component ────────────────────────────────────────────────────────────────

export const DayTimelineView = ({
    events,
    currentDay,
    isFromJob = false,
    onCreateJobAtDate,
    timeZone = "UTC",
}: DayTimelineViewProps) => {
    const theme = useTheme();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [hoveredEvent, setHoveredEvent] = useState<string | null>(null);

    const isDark = theme.palette.mode === "dark";

    const dayEvents = useMemo(
        () =>
            events.flatMap((event) => {
                const segment = visibleDaySegment(event.start, event.end, currentDay);
                return segment ? [{ ...event, ...segment }] : [];
            }),
        [events, currentDay]
    );

    const repeatedHourEvents = useMemo(() => dayEvents
        .filter(event => event.extendedProps?.event?.startDate &&
            isRepeatedCompanyInstant(event.extendedProps.event.startDate, timeZone))
        .sort((a, b) => Date.parse(a.extendedProps.event!.startDate) - Date.parse(b.extendedProps.event!.startDate)),
    [dayEvents, timeZone]);

    const workforceRows = useMemo(() => {
        const map = new Map<
            string,
            { key: string; label: string; avatar: string | null; role: string; events: CalendarEvent[] }
        >();

        for (const event of dayEvents) {
            const wfList = event.extendedProps?.workforce ?? [];

            if (wfList.length === 0) {
                const key = UNASSIGNED_LABEL;
                if (!map.has(key)) {
                    map.set(key, { key, label: UNASSIGNED_LABEL, avatar: null, role: "", events: [] });
                }
                map.get(key)!.events.push(event);
            } else {
                for (const wf of wfList) {
                    const key = wf.WorkforcesByJobEvents?.workforceUid ?? `${wf.firstName}-${wf.lastName}`;
                    if (!map.has(key)) {
                        map.set(key, {
                            key,
                            label: `${wf.firstName} ${wf.lastName}`,
                            avatar: wf.avatar,
                            role: wf.WorkforcesByJobs?.jobWorkforceRole ?? "",
                            events: [],
                        });
                    }
                    map.get(key)!.events.push(event);
                }
            }
        }

        return Array.from(map.values());
    }, [dayEvents]);

    const totalRows = Math.max(workforceRows.length, 1);
    const gridHeight = totalRows * ROW_HEIGHT;

    const hours = Array.from({ length: TOTAL_HOURS + 1 }, (_, i) => HOUR_START + i);

    // Colors
    const bg = isDark ? "#0F172A" : "#F8FAFC";
    const panelBg = isDark ? "#1E293B" : "#FFFFFF";
    const borderColor = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)";
    const headerBg = isDark ? "#1E293B" : "#FFFFFF";
    const nowLineColor = "#EF4444";

    const nowMinutes = useMemo(() => {
        const now = DateTime.now().setZone(timeZone);
        if (now.toISODate() !== currentDay.format("YYYY-MM-DD")) return null;
        return now.hour * 60 + now.minute;
    }, [currentDay, timeZone]);

    const handleEmptySlotClick = (event: MouseEvent<HTMLDivElement>) => {
        if (isFromJob || !onCreateJobAtDate) return;

        const rect = event.currentTarget.getBoundingClientRect();
        const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
        const totalMinutes = Math.round((ratio * TOTAL_HOURS * 60) / 15) * 15;
        const hour = Math.min(Math.floor(totalMinutes / 60), 23);
        const minute = hour === 23 ? Math.min(totalMinutes % 60, 45) : totalMinutes % 60;
        onCreateJobAtDate(currentDay.hour(hour).minute(minute).second(0).millisecond(0).toDate());
    };

    return (
        <Box
            sx={{
                width: "100%",
                borderRadius: "16px",
                overflow: "hidden",
                border: `1px solid ${borderColor}`,
                bgcolor: bg,
                fontFamily: "'DM Sans', sans-serif",
                boxShadow: isDark
                    ? "0 4px 32px rgba(0,0,0,0.4)"
                    : "0 4px 24px rgba(0,0,0,0.06)",
                position: "relative",
                "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "60px",
                    background: isDark
                        ? "linear-gradient(to bottom, transparent, rgba(15,23,42,0.85))"
                        : "linear-gradient(to bottom, transparent, rgba(248,250,252,0.92))",
                    pointerEvents: "none",
                    zIndex: 30,
                    borderRadius: "0 0 16px 16px",
                },
            }}
        >
            {repeatedHourEvents.length > 0 && (
                <Box sx={{ p: 2, borderBottom: `1px solid ${borderColor}`, bgcolor: panelBg }}>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 700 }}>
                        Repeated clock hour · exact visit order
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                        {repeatedHourEvents.map(event => {
                            const source = event.extendedProps.event!;
                            const label = `${formatCompanyInstant(source.startDate, timeZone)} · ${event.title}`;
                            return (
                                <BaseModalComponent
                                    key={event.id}
                                    modalKey={`Jobs-Calendar-repeated-${event.id}`}
                                    modalLabel={label}
                                    elementTrigger={<Button size="small" variant="outlined">{label}</Button>}
                                >
                                    <EventJobModal arg={{ event: { ...event, extendedProps: event.extendedProps } }} />
                                </BaseModalComponent>
                            );
                        })}
                    </Box>
                </Box>
            )}
            <Box
                sx={{
                    display: "flex",
                    position: "sticky",
                    top: 0,
                    zIndex: 20,
                    bgcolor: headerBg,
                    borderBottom: `1px solid ${borderColor}`,
                    boxShadow: isDark
                        ? "0 2px 8px rgba(0,0,0,0.3)"
                        : "0 2px 8px rgba(0,0,0,0.05)",
                }}
            >
                <Box
                    sx={{
                        minWidth: LEFT_PANEL_WIDTH,
                        width: LEFT_PANEL_WIDTH,
                        height: HEADER_HEIGHT,
                        display: "flex",
                        alignItems: "center",
                        px: 2,
                        borderRight: `1px solid ${borderColor}`,
                        flexShrink: 0,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            color: isDark ? "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.4)" : "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.35)",
                        }}
                    >
                        Workforce
                    </Typography>
                </Box>

                {/* Hour labels */}
                <Box
                    sx={{
                        flex: 1,
                        position: "relative",
                        height: HEADER_HEIGHT,
                        overflow: "hidden",
                    }}
                >
                    {hours.map((h) => (
                        <Box
                            key={h}
                            sx={{
                                position: "absolute",
                                left: pct(h * 60),
                                top: 0,
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                transform: "translateX(-50%)",
                                pointerEvents: "none",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "0.7rem",
                                    fontWeight: h === 0 || h === 24 ? 700 : 500,
                                    color:
                                        h === 0 || h === 24
                                            ? isDark
                                                ? "rgba(255,255,255,0.6)"
                                                : "rgba(0,0,0,0.55)"
                                            : isDark
                                                ? "rgba(255,255,255,0.35)"
                                                : "rgba(0,0,0,0.35)",
                                    letterSpacing: "0.04em",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* ── Scrollable body ── */}
            <Box
                ref={scrollRef}
                sx={{
                    display: "flex",
                    overflowX: "auto",
                    overflowY: "auto",
                    maxHeight: ROW_HEIGHT * ROW_HEIGHT_MULTIPLE,
                    "&::-webkit-scrollbar": { height: 6, width: 0 },
                    "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                    "&::-webkit-scrollbar-thumb": {
                        bgcolor: isDark ? "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.15)" : "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.15)",
                        borderRadius: 3,
                    },
                }}
            >
                {/* Left panel: workforce names */}
                <Box
                    sx={{
                        minWidth: LEFT_PANEL_WIDTH,
                        width: LEFT_PANEL_WIDTH,
                        flexShrink: 0,
                        bgcolor: panelBg,
                        borderRight: `1px solid ${borderColor}`,
                        position: "sticky",
                        left: 0,
                        zIndex: 10,
                    }}
                >
                    {workforceRows.length === 0 ? (
                        <Box sx={{ height: ROW_HEIGHT * ROW_HEIGHT_MULTIPLE, display: "flex", alignItems: "center", px: 2 }}>
                            <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                                No workforce
                            </Typography>
                        </Box>
                    ) : (
                        workforceRows.map((row, i) => (
                            <Box
                                key={row.key}
                                sx={{
                                    height: ROW_HEIGHT,
                                    display: "flex",
                                    alignItems: "center",
                                    px: 2,
                                    gap: 1.5,
                                    borderBottom: i < workforceRows.length - 1 ? `1px solid ${borderColor}` : "none",
                                    transition: "background 0.15s",
                                    "&:hover": {
                                        bgcolor: isDark ? "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.03)" : "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.02)",
                                    },
                                }}
                            >
                                <Avatar
                                    src={row.avatar ?? undefined}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        fontSize: "0.7rem",
                                        fontWeight: 700,
                                        bgcolor:
                                            row.label === UNASSIGNED_LABEL
                                                ? isDark
                                                    ? "#374151"
                                                    : "#E5E7EB"
                                                : "#D97706",
                                        color: row.label === UNASSIGNED_LABEL ? "text.secondary" : "#fff",
                                        flexShrink: 0,
                                        border: `2px solid ${isDark ? "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.1)" : "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)"}`,
                                    }}
                                >
                                    {row.label === UNASSIGNED_LABEL
                                        ? "?"
                                        : initials(row.label.split(" ")[0], row.label.split(" ")[1] ?? "")}
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontSize: "0.78rem",
                                            fontWeight: 600,
                                            color: isDark ? "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.85)" : "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.8)",
                                            lineHeight: 1.2,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {row.label}
                                    </Typography>
                                    {row.role && (
                                        <Typography
                                            sx={{
                                                fontSize: "0.65rem",
                                                fontWeight: 500,
                                                color: isDark ? "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.35)" : "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.4)",
                                                textTransform: "capitalize",
                                                letterSpacing: "0.03em",
                                                mt: 0.2,
                                            }}
                                        >
                                            {row.role}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        ))
                    )}
                </Box>

                <Box
                    onClick={handleEmptySlotClick}
                    sx={{
                        flex: 1,
                        minWidth: 900,
                        position: "relative",
                        height: gridHeight,
                        cursor: isFromJob ? "default" : "copy",
                    }}
                >
                    {hours.map((h) => (
                        <Box
                            key={h}
                            sx={{
                                position: "absolute",
                                left: pct(h * 60),
                                top: 0,
                                bottom: 0,
                                width: "1px",
                                bgcolor:
                                    h % 6 === 0
                                        ? isDark
                                            ? "rgba(255,255,255,0.12)"
                                            : "rgba(0,0,0,0.1)"
                                        : isDark
                                            ? "rgba(255,255,255,0.04)"
                                            : "rgba(0,0,0,0.04)",
                                pointerEvents: "none",
                                zIndex: 1,
                            }}
                        />
                    ))}

                    {workforceRows.map((_, i) => (
                        <Box
                            key={i}
                            sx={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                top: (i + 1) * ROW_HEIGHT - 1,
                                height: "1px",
                                bgcolor: borderColor,
                                zIndex: 1,
                            }}
                        />
                    ))}

                    {workforceRows.map((_, i) =>
                        i % 2 === 1 ? (
                            <Box
                                key={`bg-${i}`}
                                sx={{
                                    position: "absolute",
                                    left: 0,
                                    right: 0,
                                    top: i * ROW_HEIGHT,
                                    height: ROW_HEIGHT,
                                    bgcolor: isDark ? "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.015)" : "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.015)",
                                    zIndex: 0,
                                    pointerEvents: "none",
                                }}
                            />
                        ) : null
                    )}

                    {nowMinutes !== null && (
                        <Box
                            sx={{
                                position: "absolute",
                                left: pct(nowMinutes),
                                top: 0,
                                bottom: 0,
                                width: "2px",
                                bgcolor: nowLineColor,
                                zIndex: 8,
                                pointerEvents: "none",
                                "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    top: 0,
                                    left: "-4px",
                                    width: "10px",
                                    height: "10px",
                                    borderRadius: "50%",
                                    bgcolor: nowLineColor,
                                },
                            }}
                        />
                    )}

                    {workforceRows.map((row, rowIndex) =>
                        row.events.map((event) => {
                            const color = statusColor(
                                event.extendedProps?.status ?? "",
                                event.extendedProps?.color ?? "#6B7280"
                            );


                            return (
                                <TimelineEventCard
                                    key={`${row.key}-${event.id}`}
                                    event={event}
                                    rowIndex={rowIndex}
                                    color={color}
                                    isFromJob={isFromJob}
                                    isDark={isDark}
                                    hoveredKey={hoveredEvent}
                                    rowKey={row.key}
                                    onHover={setHoveredEvent}
                                    timeZone={timeZone}
                                />
                            );
                        })
                    )}

                    {workforceRows.length === 0 && (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                height: ROW_HEIGHT * ROW_HEIGHT_MULTIPLE,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                opacity: 0.4,
                            }}
                        >
                            <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                                No events scheduled
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};
